import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, provide, ref, watch } from 'vue';

import {
	GetTitleOffersDocument,
	GetTitleOffersQuery,
	GetTitleOffersQueryVariables,
} from '@/features/buybox/graphql/queries/GetTitleOffers.query';

import { ImpressionTrackingEvents } from '@/enums/events';
import { OfferLocation } from '@/helpers/offer-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { timestamp } from '@/helpers/date-helper';

import type { TitleOffers } from '@/interfaces/buybox';

import {
	ObjectType,
	Offer as OfferGraphql,
	Scoring,
	SraPlacement,
	MonetizationType,
	PresentationType,
	SraEventAction,
} from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';

import { CountriesToBeSorted, sortOffersByMonetizationPotential } from '@/features/buybox/helpers/buy-box-helpers';

import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import GridBuyBoxFilter from '@/features/buybox/components/grid/GridBuyBoxFilter.vue';
import OfferRow from '@/features/buybox/components/grid/OfferRow.vue';
import BuyBoxCountrySwitcher from '@/features/buybox/components/BuyBoxCountrySwitcher.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { BuyboxPresentationType, useUserStore } from '@/stores/pinia/useUserStore';
import { qtvProviders, qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';
import { useGridCategorySplit } from '@/features/buybox/composables/useBuyboxView';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useExperiment } from '@/libraries/experiment';
import GridRowLabel from '@/features/buybox/components/grid/GridRowLabel.vue';
import GridSwiperExp from '@/components/experiments/GridSwiperExp/GridSwiperExp.vue';
import { useJWTVCampaign } from '@/features/jwtv-titles-rental/composables/useJWTVCampaign';
import { useJWTVUtopia, JWTV_UTOPIA_SHORTNAME } from '@/helpers/ad/useJWTVUtopiaTargeting';
import { useMonolithTargeting, MONOLITH_SHORTNAME } from '@/helpers/ad/useMonolithTargeting';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import GridPreRollExp from '@/components/experiments/PreRollAndStaticBBAdsExp/GridPreRollExp.vue';
import { useAdServer, useVerifyAdServerResult } from '@/features/ad-server/composables/useAdServer';
import { AdServerCreativeType } from '@/features/ad-server/types';

// EXP_PROVIDER_BUYBOX_V2
import { useProviderBuyboxFilter } from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxFilter';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';

export default /*#__PURE__*/_defineComponent({
  __name: 'GridBuyBox',
  props: {
    titleId: null,
    titleObjectType: null,
    titleObjectId: null,
    titleName: { default: '' },
    title: null,
    showDetails: { default: null },
    isTitleInWatchlist: { type: Boolean },
    titleScoring: { default: () => ({}) },
    seasonNumber: { default: null },
    episodeNumber: { default: null },
    originalReleaseYear: { default: undefined },
    sponsoredAd: { default: undefined },
    isBannerBuyBox: { type: Boolean },
    additionalContexts: { default: () => [] },
    buyBoxSwitchType: null,
    showFilter: { type: Boolean },
    inline: { type: Boolean },
    noHeader: { type: Boolean },
    compact: { type: Boolean },
    defaultPresentationType: null,
    isInModal: { type: Boolean },
    isAVODActive: { type: Boolean },
    isEpisodeBuybox: { type: Boolean },
    placement: { default: 'regular_buybox' },
    index: null
  },
  emits: ["onWatchlistToggle", "buyboxLoading"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onWatchlistToggle', createdAt: string | null): void;
	(e: 'buyboxLoading', isLoading: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { titleId: string, titleObjectType: ObjectType, titleObjectId: number, titleName: string, title: TitleDetail, showDetails: {
			id: string;
			objectId: number;
			objectType: ObjectType;
			title: string;
		} | null, isTitleInWatchlist?: boolean, titleScoring: Scoring, seasonNumber: number | null, episodeNumber: number | null, originalReleaseYear: number | null, sponsoredAd: SponsoredAdFragment, isBannerBuyBox?: boolean, additionalContexts: SnowplowContext[], buyBoxSwitchType?: NewBuyBoxView, showFilter?: boolean, inline?: boolean, noHeader?: boolean, compact?: boolean, defaultPresentationType?: BuyboxPresentationType, isInModal?: boolean, isAVODActive?: boolean, isEpisodeBuybox?: boolean, placement: ClickoutPlacement, index?: number };

const SaveServicesPrompt = () => import('@/components/experiments/ProviderBuyboxExp/components/SaveServicesPrompt.vue');
// EXP_PROVIDER_BUYBOX_V2
const OfferReporter = () => import('@/features/buybox/components/OfferReporter.vue');
const FastOfferRow = () => import('@/features/buybox/components/grid/FastOfferRow.vue');
const NoOfferRow = () => import('@/features/buybox/components/grid/NoOfferRow.vue');
// EXP_PROVIDER_BUYBOX_V2
const NoProviderOffers = () => import('@/features/buybox/components/NoProviderOffers.vue');
// EXP_PROVIDER_BUYBOX_V2
const GridPromotionRow = () => import('@/features/buybox/components/grid/GridPromotionRow.vue');
const AdServerGridPromotionPlacement = () =>
	import('@/features/ad-server/components/AdServerGridPromotionPlacement.vue');
const QualityTvRow = () => import('@/features/buybox/components/grid/QualityTvRow.vue');
const GridSQTV = () => import('@/features/buybox/components/grid/GridSQTV.vue');
const GridOfferRowRedesignExp = () =>
	import('@/components/experiments/GridBuyboxRedesignExps/components/GridOfferRowRedesignExp.vue');
const GridFreeTrialCardV2 = () =>
	import('@/components/experiments/GridBuyboxRedesignExps/components/GridFreeTrialCardV2.vue');
const SponsoredRecommendation = () =>
	import('@/features/sponsored-recommendations/components/SponsoredRecommendation.vue');





const episodeBuyBoxPresentationType = ref<BuyboxPresentationType | null>(null);

const { titleProviderCount } = storeToRefs(useConstantStore());
const { language, country } = storeToRefs(useLanguageStore());

const userStore = useUserStore();
const { setBuyboxPresentationType } = userStore;
const { buyboxPresentationType, isPremium, isProUser } = storeToRefs(userStore);
const { pagesVisited } = storeToRefs(useTrackingStore());

const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();
const { trackSraEvent } = useBackendTracking();

const { hasActiveJwtvCampaign } = useJWTVCampaign();

/** Boolean value to only enable SQTV SRs on Buybox rendered on title detail page (Excludes: banners, search and editorial).
 * Notify SG Team if SQTV SR is to be extended to search page */
const isSREnabled = computed(() => {
	const routeName = activeRoute.value?.name;
	const isDetailPage = routeName?.includes('app.detail');
	const isSearchPage = routeName?.includes('app.search.list');

	return !!(isDetailPage && !isSearchPage && !props.inline && !props.isBannerBuyBox);
});

const { sponsoredAd, isShadowCampaign, hasActiveSRCampaign } = useSendBidId({
	pageType: 'VIEW_BUYBOX',
	placement: SraPlacement.Buybox,
	jwEntityID: props.title.id,
	isEnabled: isSREnabled,
	isSeo: pagesVisited.value <= 1,
	buyBoxType: NewBuyBoxView.GRID,
});

const hasSponsoredRecommendation = computed(() => {
	return hasActiveSRCampaign.value && !isShadowCampaign.value;
});

useProvideClickoutPlacement(() => props.placement);

provide('isBannerBuyBox', () => props.isBannerBuyBox);
provide('titleSourceId', props.titleId);
provide('buyboxIndex', () => props.index);

const buyBoxActiveType = computed(() => {
	return props.isEpisodeBuybox ? episodeBuyBoxPresentationType.value : buyboxPresentationType.value;
});

const presentationTypeGraphql = computed(() => {
	const presentationTypeMap: Partial<Record<PresentationType, PresentationType[]>> = {
		[PresentationType['4K']]: [PresentationType['4K'], PresentationType.Bluray_4K],
		[PresentationType.Hd]: [PresentationType.Hd, PresentationType.Bluray],
		[PresentationType.Sd]: [PresentationType.Sd, PresentationType.Dvd],
		[PresentationType.Dvd]: [PresentationType.Dvd],
		[PresentationType.Bluray]: [PresentationType.Bluray, PresentationType.Bluray_4K],
		[PresentationType.Canvas]: [PresentationType.Canvas],
	};

	if (buyBoxActiveType.value == null) return null;
	if (buyBoxActiveType.value == MonetizationType.Free) return null;
	return presentationTypeMap[buyBoxActiveType.value];
});

const queryVariables = computed(() => {
	//prettier-ignore
	const presentationTypes = (presentationTypeGraphql.value ?? [])?.length > 0 ? presentationTypeGraphql.value : undefined;
	const dvdPresentationTypes = presentationTypes
		? presentationTypes
		: [PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K];
	return {
		nodeId: props.titleId,
		country: country.value,
		language: language.value,
		filterBuy: {
			monetizationTypes: [MonetizationType.Buy],
			bestOnly: true,
			preAffiliate: true,
			presentationTypes,
		},
		filterFlatrate: {
			monetizationTypes: [
				MonetizationType.Flatrate,
				MonetizationType.FlatrateAndBuy,
				MonetizationType.Ads,
				MonetizationType.Free,
				MonetizationType.Cinema,
			],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterRent: {
			monetizationTypes: [MonetizationType.Rent],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterFree: {
			monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
			presentationTypes,
			bestOnly: true,
			preAffiliate: true,
		},
		filterDvdBluRay: {
			monetizationTypes: [MonetizationType.Rent, MonetizationType.Buy],
			presentationTypes: dvdPresentationTypes,
			preAffiliate: true,
		},
		filterBundles: {
			presentationTypes,
			bestOnly: true,
		},
	};
});

const { loading, result, onResult } = useQuery<GetTitleOffersQuery, GetTitleOffersQueryVariables>(
	GetTitleOffersDocument,
	queryVariables
);
// Cached Results are used to avoid flickering when user changes the selected Tab as it triggers the query again and result set to null.
const cachedResults = ref<GetTitleOffersQuery | null>(null);
onResult(data => {
	if (!data.loading) {
		cachedResults.value = data.data ?? null;
	}
});

const offers = computed(() => {
	const node = (result.value ?? cachedResults.value)?.node;
	if (
		node &&
		(node.__typename === 'Episode' ||
			node.__typename === 'Movie' ||
			node.__typename === 'Season' ||
			node.__typename === 'Show')
	) {
		const { fast, free, flatrate, offerCount, maxOfferUpdatedAt, bundles } = node;
		const { rent, buy } = getRentAndBuyOffers(node);
		const res: TitleOffers = {
			stream: [],
			rent: rent,
			buy: buy,
			free: free,
			fast: fast,
			cinema: [],
			bundles: bundles as TitleOffers['bundles'],
			offerCount,
			maxOfferUpdatedAt,
		};
		flatrate.forEach(offer =>
			offer.monetizationType === MonetizationType.Cinema ? res.cinema.push(offer) : res.stream.push(offer)
		);
		return res;
	}

	return {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		bundles: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};
});

const { inPromotionCatalogue, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => {
		const { free = [], stream } = offers.value;
		return [...free, ...stream];
	},
});

// Grid Swiper Exp
const { activeVariantWithControlGroup: expGridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');
const expGridSwiperActive = computed(
	() => !props.inline && expGridSwiperVariant.value && expGridSwiperVariant.value !== 'control'
);

// Grid Swiper Exp

// Pre Roll & Static BB Ads Exp
const { activeVariantWithControlGroup: underperformingUrlsExpVariant } = useExperiment('EXP_UNDERPERFORMING_URLS_5');
const underperformingUrlsExp5Active = computed(
	() => !props.inline && underperformingUrlsExpVariant.value && underperformingUrlsExpVariant.value !== 'control'
);
// Pre Roll & Static BB Ads Exp

// Grid Redesign Exps

const { activeVariantWithControlGroup: gridMoreInfoVariant } = useExperiment('EXP_GRID_MORE_INFO_REDESIGN_V2');

const renderGridRedesign = computed(() => {
	if (props.inline) return false;
	return gridMoreInfoVariant.value && gridMoreInfoVariant.value !== 'control';
});

const renderSponsoredRecommendation = computed(() => hasSponsoredRecommendation.value && isSREnabled.value);

const isFreeBuyBox = computed(() => {
	return buyBoxActiveType.value === MonetizationType.Free;
});

// EXP_PROVIDER_BUYBOX_V2
const buyboxOfferProviders = computed(() => {
	const {
		bundles: _bundles,
		offerCount: _offerCount,
		maxOfferUpdatedAt: _maxOfferUpdatedAt,
		...offersByType
	} = offers.value;
	return [
		...new Set(
			(
				Object.values(offersByType) as Array<
					Array<{ package: { shortName: string }; presentationType: PresentationType }>
				>
			)
				.flat()
				.filter(
					offer =>
						![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(
							offer.presentationType
						)
				)
				.map(offer => offer.package.shortName)
		),
	];
});
const { isActive: isInternationalOffersActive } = useInternationalOffersStatus({ title: () => props.title });
const { selectedBuyboxProviders, showSavePrompt, onSaveServicesSignUp } = useProviderBuyboxFilter({
	offerProviders: buyboxOfferProviders,
	isInternationalOffersActive,
});
// EXP_PROVIDER_BUYBOX_V2

const filteredOffers = computed(() => {
	// Start with the full list of offers
	let _offers = { ...offers.value };

	// Filtering out TVOD providers we don't have a partnership with
	const { offerCount, maxOfferUpdatedAt, bundles, ...allOffers } = _offers;

	let offerEntries = Object.entries(allOffers);

	// If the title has a JWTV offer and there is an active JWTV campaign, only show JWTV offers.
	const isJWTVTitleCampaign =
		offers.value.rent.some(offer => offer.package.shortName === 'jwt') && hasActiveJwtvCampaign.value;

	if (isJWTVTitleCampaign) {
		offerEntries = offerEntries.map(([key, value]) => [
			key,
			value.filter(offer => offer.package.shortName === 'jwt'),
		]);
	}

	// EXP_PROVIDER_BUYBOX_V2
	if (selectedBuyboxProviders.value && selectedBuyboxProviders.value.length > 0) {
		offerEntries = offerEntries.map(([key, value]) => [
			key,
			value.filter(offer => selectedBuyboxProviders.value!.includes(offer.package.shortName)),
		]);
	}
	// EXP_PROVIDER_BUYBOX_V2

	_offers = {
		...Object.fromEntries(offerEntries),
		// EXP_PROVIDER_BUYBOX_V2
		bundles:
			selectedBuyboxProviders.value && selectedBuyboxProviders.value.length > 0
				? bundles?.filter(bundle =>
						bundle.node.packages.some(pkg => selectedBuyboxProviders.value!.includes(pkg.shortName))
				  )
				: bundles,
		// EXP_PROVIDER_BUYBOX_V2
		offerCount,
		maxOfferUpdatedAt,
	} as TitleOffers;

	if (!props.isBannerBuyBox) return _offers;

	// Return filtered offers
	return sortOffersByMonetizationPotential(_offers, country.value as CountriesToBeSorted);
});

// Tubi Test >> for US only, sort buybox offers to show Tubi First
const filteredOffersFree = computed(() => filteredOffers.value.free);

const filteredOffersSorted = computed(() => filteredOffers.value.stream);
// Tubi Test

/** Excludes Free. The title does not have stream, rent, buy, or any other offers. */
const hasNoNonFreeOffers = computed(() => {
	return (
		(['stream', 'rent', 'buy', 'fast', 'cinema'] as const).reduce(
			(acc, curr) => (acc += (filteredOffers.value?.[curr] ?? []).length),
			0
		) === 0
	);
});

const hasOnlyCinemaOffers = computed(() => {
	const cinemaOfferCount = filteredOffers.value.cinema.length;

	const hasNoNonCinemaOffers =
		(['stream', 'rent', 'buy', 'fast', 'free', 'fast', 'bundles'] as const).reduce(
			(acc, curr) => (acc += (filteredOffers.value?.[curr] ?? []).length),
			0
		) === 0;

	return cinemaOfferCount > 0 && hasNoNonCinemaOffers;
});

const showPromotionRow = computed(() => {
	const rawLanguage = language.value.split('-')[0];
	if (!props.title || isPremium.value || !allowedPromotionLanguages.includes(rawLanguage)) return false;
	return isFreeBuyBox.value;
});

const titleContext = computed(() => {
	const titleObjectId = props.showDetails ? props.showDetails.objectId : props.titleObjectId;
	const titleObjectType = props.showDetails ? props.showDetails.objectType : props.titleObjectType;
	return new SnowplowTitleContextGraphql(
		titleObjectId,
		titleObjectType,
		props.seasonNumber,
		props.episodeNumber,
		null,
		props.index
	);
});

provide('titleContext', titleContext);

const { isJWTVUtopiaActive } = useJWTVUtopia(
	() => props.title,
	() => !props.inline
);
const { isMonolithActive } = useMonolithTargeting(
	() => props.title,
	() => !props.inline
);

const hasNoOffersToShow = computed(() => {
	// this buybox filter is about Monetization, so here specifically we ask
	// if there are no "FREE" offers only
	if (isFreeBuyBox.value) {
		const hasNoFreeOffers = filteredOffers.value.free.length === 0;

		// Temporary - we are disabling FT when SR active, until the swiper is available
		if (renderSponsoredRecommendation.value) return hasNoFreeOffers;

		// if there is a promotion from a provider that does have an offer,
		// then technically the promotion counts as an "offer".
		if (showPromotionRow.value) {
			return (!inPromotionCatalogue.value || isJWTVUtopiaActive.value) && hasNoFreeOffers;
		}

		return hasNoFreeOffers;
	}

	return hasNoNonFreeOffers.value || hasOnlyCinemaOffers.value;
});

// EXP_PROVIDER_BUYBOX_V2: cinema offers are shown in their own row, so don't show this
// when the only reason hasNoOffersToShow is true is that cinema offers exist.
const showNoProviderOffers = computed(
	() => selectedBuyboxProviders.value !== null && hasNoOffersToShow.value && filteredOffers.value.cinema.length === 0
);
// EXP_PROVIDER_BUYBOX_V2

const lastUpdated = computed(() => {
	return filteredOffers.value.maxOfferUpdatedAt;
});

const isTitleDetailPage = computed(() => {
	return activeRoute.value?.name?.includes('app.detail');
});

const timestampText = computed(() => {
	const updated = timestamp(
		lastUpdated.value,
		{ year: 'numeric', month: 'long', day: 'numeric' },
		country.value,
		language.value
	);
	return t('WEBAPP_BUYBOX_UPDATED_TIMESTAMP', {
		services_total: titleProviderCount.value,
		date: updated.date,
		time: updated.time,
	});
});

const sponsoredRecommendationPackageId = computed(() => {
	return props.sponsoredAd?.campaign?.watchNowOffer.package.packageId;
});

const sponsoredRecommendationCohort = computed(() => {
	return props.sponsoredAd?.holdoutGroup ? 0 : 1;
});

const offerReporterTitleOffers = computed(() => {
	return [
		...filteredOffers.value.free,
		...filteredOffers.value.stream,
		...filteredOffers.value.rent,
		...filteredOffers.value.buy,
		...filteredOffers.value.cinema,
	];
});

const fastOffers = computed(() => {
	// Exclude Offers that are no longer active.
	return filteredOffers.value.fast.filter(el => new Date(el.availableToTime) > new Date());
});

const showFastRow = computed(() => {
	return isFreeBuyBox.value || buyBoxActiveType.value === null;
});

const trackingContexts = computed(() => (props.additionalContexts || []).concat([titleContext.value]));

const hideTVODQTV = computed(() => {
	return (
		catalogueItem.value?.placement === 'qtv' &&
		!['FREE', 'JWTV'].includes(qtvProviderType[catalogueItem.value.shortName]) &&
		isFreeBuyBox.value
	);
});

const promotionProviderShortName = computed(() => {
	if (isJWTVUtopiaActive.value) return JWTV_UTOPIA_SHORTNAME;
	if (isMonolithActive.value) return MONOLITH_SHORTNAME;
	return catalogueItem.value?.shortName;
});

const qualityTvActive = computed(() => {
	if (hasSponsoredRecommendation.value) return false;
	if (expGridSwiperActive.value) return false;
	if (gridMoreInfoVariant.value) return false;
	if (underperformingUrlsExp5Active.value) return false;
	return (
		!isProUser.value &&
		!props.inline &&
		!props.isBannerBuyBox &&
		// either placement is overwritten by yo, or the programmatic decision checks
		catalogueItem.value &&
		// show QTV on old buybox when SQTV is active
		catalogueItem.value.placement === 'qtv' &&
		qtvProviders.includes(catalogueItem.value.shortName) &&
		// Itunes TVOD QTV is not a Free Trial, cannot be shown when filter is Free.
		!hideTVODQTV.value
	);
});

const isSqtvActive = computed(() => {
	if (hasSponsoredRecommendation.value) return false;
	if (expGridSwiperActive.value) return false;
	if (gridMoreInfoVariant.value) return false;
	if (underperformingUrlsExp5Active.value) return false;
	if (isProUser.value || props.inline) return false;

	if (isJWTVUtopiaActive.value) return true;
	if (isMonolithActive.value) return true;

	return !!catalogueItem.value && catalogueItem.value.placement === 'sqtv';
});

const { isSQTVEmpty } = useSingleQTV(
	() => props.title,
	() => promotionProviderShortName.value || null,
	isSqtvActive
);

const showSQTV = computed(() => isSqtvActive.value && !isSQTVEmpty.value);

const freeTrialActive = computed(() => {
	if (expGridSwiperActive.value) return false;
	if (gridMoreInfoVariant.value) return true;
	if (hasSponsoredRecommendation.value) return false;
	if (!!catalogueItem.value?.placement) {
		if (isSqtvActive.value) return isSQTVEmpty.value;
		// additional exception: banner, search & guide page buyboxes always render FT
		return !!props.inline || catalogueItem.value?.placement === 'ft';
	}

	return !catalogueItem.value?.placement && !qualityTvActive.value && !showSQTV.value;
});

const { adServerStatus, adServerCreative } = useAdServer(() => props.title.id);

/* Experiments x Ad Serving - If an experiment wants to render a potentially different placement/provider than expected from YO config/Drip setup, shortcut the ad serving here */
const activeExperimentWithAffiliateOverride = computed(
	() => !!underperformingUrlsExpVariant.value || !!gridMoreInfoVariant.value
);
const overrideAdServerPlacement = computed(() => {
	if (sponsoredAd.value?.campaign) return true;
	if (activeExperimentWithAffiliateOverride.value) return true;
	if (isJWTVUtopiaActive.value) return true;
	return false;
});

const renderAdServerGridPromotion = computed(() => {
	return false;
	if (isProUser.value) return false;
	if (props.inline) return false;
	if (overrideAdServerPlacement.value) return false;

	if (adServerStatus.value === 'disabled' || adServerStatus.value === 'failed') return false;

	// Only render FTs on Ad Server for first iteration
	return adServerCreative.value?.__typename === AdServerCreativeType.FREE_TRIAL;
});

// For verification of Ad Server placement only - migration requirement.
const gridAdServerPlacement = computed(() => {
	if (showSQTV.value) return 'sqtv';
	if (qualityTvActive.value) return 'qtv';
	if (freeTrialActive.value) return 'ft';
	return null;
});

useVerifyAdServerResult({
	placement: gridAdServerPlacement,
	providers: () => [catalogueItem.value?.shortName],
	title: () => props.title,
	enabled: () => !props.inline,
});

const showGridExpV2SimilarRow = computed(() => {
	if (gridMoreInfoVariant.value !== 'variant_2') return false;
	if (!props.title || isPremium.value || props.inline) return false;
	return freeTrialActive.value;
});

const promotionShortName = computed(() => {
	if (!!catalogueItem.value?.placement) {
		return catalogueItem.value.shortName;
	}

	return null;
});

// Grid Redesign Exps
const offerRows = computed(() => [
	{
		key: 'stream',
		class: 'stream',
		offers: isFreeBuyBox.value ? filteredOffersFree.value : filteredOffersSorted.value,
		bundles: isFreeBuyBox.value ? [] : filteredOffers.value.bundles,
		label: isFreeBuyBox.value ? 'WEBAPP_FREE' : 'WEBAPP_STREAM',
		show: true,
	},
	{
		key: 'rent',
		class: 'rent',
		offers: filteredOffers.value.rent,
		label: 'WEBAPP_RENT',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'buy',
		class: 'buy',
		offers: filteredOffers.value.buy,
		label: 'WEBAPP_BUY',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'cinema',
		class: 'cinema',
		offers: filteredOffers.value.cinema,
		label: 'WEBAPP_CINEMA',
		show: !isFreeBuyBox.value,
	},
	{
		key: 'fast',
		class: 'fast',
		offers: fastOffers.value,
		label: 'WEBAPP_FREE_TV',
		show: showFastRow.value,
	},
]);
// Grid Redesign Exps

function setEpisodeBuyboxPresentationType(presentationType: BuyboxPresentationType | null) {
	episodeBuyBoxPresentationType.value = presentationType;
}

function clickOut(payload: { buyboxOffer: OfferGraphql; isLeavingIconShown: boolean; offerLocation?: OfferLocation }) {
	const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer);

	if (
		!!sponsoredRecommendationPackageId.value &&
		payload.buyboxOffer.package.packageId === sponsoredRecommendationPackageId.value
	) {
		let contexts: SnowplowContext[] = [];
		if (payload.buyboxOffer) {
			contexts = trackingContexts.value;
			contexts.push(clickoutContext);
		}

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'buybox_clickout',
				label: `${props.titleId}_${sponsoredRecommendationPackageId.value}`,
				property: 'buybox',
				value: sponsoredRecommendationCohort.value,
			},
			contexts
		);

		trackSraEvent({
			action: SraEventAction.BuyboxClickout,
			sponsoredAd: props.sponsoredAd,
			jwEntityID: props.titleId,
		});
	}
}

function getRentAndBuyOffers(
	titleOffers: AssertByTypename<GetTitleOffersQuery['node'], 'Episode' | 'Movie' | 'Season' | 'Show'>
) {
	const isDigital = (offer: any) =>
		![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(offer.presentationType);

	const dvdRent = titleOffers.dvdBluRay
		.filter(o => o.monetizationType === MonetizationType.Rent && !isDigital(o))
		.sort((a, b) => (a.retailPrice || '').localeCompare(b.retailPrice || '', undefined, { numeric: true }));

	const dvdBuy = titleOffers.dvdBluRay.filter(o => o.monetizationType === MonetizationType.Buy && !isDigital(o));

	return {
		rent: [...titleOffers.rent.filter(isDigital), ...dvdRent],
		buy: [...titleOffers.buy.filter(isDigital), ...dvdBuy],
	};
}

watch(loading, () => {
	if (loading.value) {
		emits('buyboxLoading', true);
	}
});

onMounted(() => {
	if (activeRoute.value?.query.monetization_types === 'free') {
		setBuyboxPresentationType({ presentationType: MonetizationType.Free });
	}
});

const { isGridCategorySplitActive } = useGridCategorySplit({
	objectType: () => props.titleObjectType,
	buyBoxActiveType: buyBoxActiveType,
});

const subscriptionOffers = computed(() => {
	return filteredOffers.value.stream.filter(
		offer =>
			offer.monetizationType === MonetizationType.Flatrate ||
			offer.monetizationType === MonetizationType.FlatrateAndBuy
	);
});

return { __sfc: true,SaveServicesPrompt, OfferReporter, FastOfferRow, NoOfferRow, NoProviderOffers, GridPromotionRow, AdServerGridPromotionPlacement, QualityTvRow, GridSQTV, GridOfferRowRedesignExp, GridFreeTrialCardV2, SponsoredRecommendation, props, emits, episodeBuyBoxPresentationType, titleProviderCount, language, country, userStore, setBuyboxPresentationType, buyboxPresentationType, isPremium, isProUser, pagesVisited, activeRoute, t, trackSraEvent, hasActiveJwtvCampaign, isSREnabled, sponsoredAd, isShadowCampaign, hasActiveSRCampaign, hasSponsoredRecommendation, buyBoxActiveType, presentationTypeGraphql, queryVariables, loading, result, onResult, cachedResults, offers, inPromotionCatalogue, catalogueItem, expGridSwiperVariant, expGridSwiperActive, underperformingUrlsExpVariant, underperformingUrlsExp5Active, gridMoreInfoVariant, renderGridRedesign, renderSponsoredRecommendation, isFreeBuyBox, buyboxOfferProviders, isInternationalOffersActive, selectedBuyboxProviders, showSavePrompt, onSaveServicesSignUp, filteredOffers, filteredOffersFree, filteredOffersSorted, hasNoNonFreeOffers, hasOnlyCinemaOffers, showPromotionRow, titleContext, isJWTVUtopiaActive, isMonolithActive, hasNoOffersToShow, showNoProviderOffers, lastUpdated, isTitleDetailPage, timestampText, sponsoredRecommendationPackageId, sponsoredRecommendationCohort, offerReporterTitleOffers, fastOffers, showFastRow, trackingContexts, hideTVODQTV, promotionProviderShortName, qualityTvActive, isSqtvActive, isSQTVEmpty, showSQTV, freeTrialActive, adServerStatus, adServerCreative, activeExperimentWithAffiliateOverride, overrideAdServerPlacement, renderAdServerGridPromotion, gridAdServerPlacement, showGridExpV2SimilarRow, promotionShortName, offerRows, setEpisodeBuyboxPresentationType, clickOut, getRentAndBuyOffers, isGridCategorySplitActive, subscriptionOffers, SraPlacement, NewBuyBoxView, GridBuyBoxFilter, OfferRow, BuyBoxCountrySwitcher, GridRowLabel, GridSwiperExp, GridPreRollExp }
}

})